import axios from 'axios'
import {
  STORE_KEY_ACCOUNT,
  STORE_KEY_AUTH,
  STORE_KEY_ID_LIST,
  STORE_KEY_USER
} from 'Constants/'

import extractApiData from 'Helpers/extractApiData'

import { sessionStoreGet, storeGet } from 'mailup-react-helpers'

import { logApiError, logApiTiming } from 'Helpers/logger'

const isNetworkError = err => !!err.isAxiosError && !err.response && err.config

const makeApiWrapper = () => {
  const _axios = axios.create()

  _axios.interceptors.request.use(config => {
    return {
      ...config,
      _start: Date.now()
    }
  })

  _axios.interceptors.response.use(
    response => {
      if (response && response.config && response.config._start) {
        const { hostname: apiHostname, pathname: apiPathname } = new URL(
          response.config.url
        )

        logApiTiming({
          apiTime: Date.now() - response.config._start,
          apiUrl: response.config.url,
          apiMethod: response.config.method,
          apiHostname,
          apiPathname
        })
      }
      return response
    },
    error => {
      if (isNetworkError(error)) {
        const { config } = error
        const { hostname: apiHostname, pathname: apiPathname } = new URL(
          config.url
        )

        logApiError({
          apiTime: Date.now() - config._start,
          apiUrl: config.url,
          apiMethod: config.method,
          apiHostname,
          apiPathname,
          apiError: JSON.stringify(error.message),
          apiNetworkError: true
        })

        return Promise.reject(error)
      }

      if (!(error && error.response && error.response.config)) {
        return Promise.reject(error)
      }

      const { hostname: apiHostname, pathname: apiPathname } = new URL(
        error.response.config.url
      )

      logApiError({
        apiTime: Date.now() - error.response.config._start,
        apiUrl: error.response.config.url,
        apiMethod: error.response.config.method,
        apiHostname,
        apiPathname,
        apiError: JSON.stringify(error.response.data)
      })
      //
      return Promise.reject(error.response)
    }
  )

  return _axios
}

const apiWrapper = makeApiWrapper()

const getClientId = () => {
  return process.env.APP_ENV === 'DEV' ? 'Int' : 'MailUp'
}

const collaborationApiAuth = ({ forcedEmail } = {}) => {
  const url = `${process.env.COLLABORATION_AUTH_URL}/api/token/oneshot/oauth2?dataFormat=Json`

  const userEmail = forcedEmail || storeGet(STORE_KEY_USER).RecoverEmail
  const account = storeGet(STORE_KEY_ACCOUNT)
  const idList = storeGet(STORE_KEY_ID_LIST)
  const accountReference = `${account.Id}_${idList}`
  const authToken = sessionStoreGet(STORE_KEY_AUTH)
  const expirationDate = new Date(Date.now() + 86400 * 1000)
    .toISOString()
    .replace(/\.\d{3}Z$/, 'Z')

  const params = new URLSearchParams()
  params.append('grant_type', 'client_credentials')
  params.append('client_id', getClientId())
  params.append('oauth2token', authToken)
  params.append('user_email', userEmail)
  params.append('account_reference', accountReference)
  params.append('expiration_date', expirationDate)

  return apiWrapper({
    method: 'post',
    url: url,
    data: params
  })
    .then(extractApiData)
    .then(apiResponse => {
      const tokenUrl = `${process.env.COLLABORATION_AUTH_URL}/token`
      const tokenParams = new URLSearchParams()

      tokenParams.append('grant_type', 'customers_token')
      tokenParams.append('client_id', getClientId())
      tokenParams.append('token', apiResponse.Data.Token)
      tokenParams.append('user_email', userEmail)
      tokenParams.append('account_reference', accountReference)

      return apiWrapper({
        method: 'post',
        url: tokenUrl,
        data: tokenParams
      })
    })
    .then(extractApiData)
}

export const collaborationApiGetAccountProjects = (states, references) => {
  return collaborationApiAuth()
    .then(auth => {
      const statesParam = states.map(s => `states=${encodeURIComponent(s)}`)
      const referencesParam = references.map(
        r => `references=${encodeURIComponent(r)}`
      )
      const params = [...statesParam, ...referencesParam].join('&')
      const url = `${process.env.COLLABORATION_API_URL}/api/Project?${params}`

      return apiWrapper({
        method: 'get',
        url: url,
        headers: {
          Authorization: `Bearer ${auth.access_token}`
        }
      })
    })
    .then(({ data, request }) => {
      //  network error
      return data || { Items: [] }
    })
    .catch(e => {
      if (e.status === 404) {
        return { Items: [] }
      } else {
        throw e
      }
    })
}

export const collaborationApiGetProject = idProject => {
  return collaborationApiAuth()
    .then(auth => {
      const url = `${
        process.env.COLLABORATION_API_URL
      }/api/Project/${encodeURIComponent(idProject)}`

      return apiWrapper({
        method: 'get',
        url: url,
        headers: {
          Authorization: `Bearer ${auth.access_token}`
        }
      })
    })
    .then(extractApiData)
}

export const collaborationApiGetParticipant = idParticipant => {
  return collaborationApiAuth().then(auth => {
    const url = `${
      process.env.COLLABORATION_API_URL
    }/api/Participant/${encodeURIComponent(idParticipant)}`

    return apiWrapper({
      method: 'get',
      url: url,
      headers: {
        Authorization: `Bearer ${auth.access_token}`
      }
    }).then(extractApiData)
  })
}

export const collaborationApiDeleteProject = idProject => {
  return collaborationApiGetProject(idProject)
    .then(project => collaborationApiGetParticipant(project.Data.IdOwners[0]))
    .then(owner => collaborationApiAuth({ forcedEmail: owner.Data.Email }))
    .then(auth => {
      const url = `${
        process.env.COLLABORATION_API_URL
      }/api/Project/${encodeURIComponent(idProject)}`

      return apiWrapper({
        method: 'delete',
        url: url,
        headers: {
          Authorization: `Bearer ${auth.access_token}`
        }
      })
    })
    .then(extractApiData)
}

export const collaborationApiGetLoginData = data => {
  const url = `${process.env.COLLABORATION_AUTH_URL}/api/token/oneshot/oauth2?dataFormat=Json`

  const userEmail = storeGet(STORE_KEY_USER).RecoverEmail
  const account = storeGet(STORE_KEY_ACCOUNT)
  const idList = storeGet(STORE_KEY_ID_LIST)
  const accountReference = `${account.Id}_${idList}`
  const authToken = sessionStoreGet(STORE_KEY_AUTH)
  const expirationDate = new Date(Date.now() + 86400 * 1000)
    .toISOString()
    .replace(/\.\d{3}Z$/, 'Z')

  const params = new URLSearchParams()
  params.append('grant_type', 'client_credentials')
  params.append('client_id', getClientId())
  params.append('oauth2token', authToken)
  params.append('user_email', userEmail)
  params.append('account_reference', accountReference)
  params.append('expiration_date', expirationDate)
  params.append('data', JSON.stringify(data))
  return apiWrapper({
    method: 'post',
    url: url,
    data: params
  }).then(extractApiData)
}
